import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { maestroIdOverride } from '../../../environments/maestro-prueba';
import { LoginResponse } from '../../modules/autenticacion/models/login.model';

/** Id de maestro local cuando el archivo de prueba trae un número válido. */
function idMaestroPrueba(): number | null {
  // Comentario: en producción no se sustituye el maestro del login.
  if (environment.production || maestroIdOverride == null || maestroIdOverride <= 0) {
    return null;
  }
  return maestroIdOverride;
}

/** Claves de sessionStorage para la sesión del portal. */
const STORAGE_KEYS = {
  jwt: 'portal_jwt',
  maestroId: 'portal_maestro_id',
  correo: 'portal_correo',
  rfc: 'portal_rfc',
  nombre: 'portal_nombre',
  role: 'portal_role',
  resetPasswd: 'portal_reset_passwd',
  loginAtMs: 'portal_login_at_ms',
} as const;

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  /** Persiste la sesión del maestro tras login exitoso. */
  startSession(data: LoginResponse): void {
    const maestroId = idMaestroPrueba() ?? data.maestro_id;
    sessionStorage.setItem(STORAGE_KEYS.jwt, data.token ?? '');
    sessionStorage.setItem(STORAGE_KEYS.maestroId, String(maestroId ?? ''));
    sessionStorage.setItem(STORAGE_KEYS.correo, data.correo ?? '');
    sessionStorage.setItem(STORAGE_KEYS.rfc, data.rfc ?? '');
    sessionStorage.setItem(STORAGE_KEYS.nombre, data.nombre ?? '');
    sessionStorage.setItem(STORAGE_KEYS.role, data.role ?? '');
    sessionStorage.setItem(STORAGE_KEYS.resetPasswd, String(data.reset_passwd ?? 0));
    sessionStorage.setItem(STORAGE_KEYS.loginAtMs, String(Date.now()));
  }

  /** Indica si hay sesión activa con JWT almacenado. */
  isAuthenticated(): boolean {
    return !!this.getJwt();
  }

  /** Obtiene el JWT del usuario autenticado. */
  getJwt(): string | null {
    return sessionStorage.getItem(STORAGE_KEYS.jwt);
  }

  /** Obtiene el identificador del maestro en sesión, o el override local si existe. */
  getMaestroId(): number {
    // Comentario: el archivo de prueba gana sobre el id guardado en el login.
    const override = idMaestroPrueba();
    if (override != null) {
      return override;
    }
    const value = sessionStorage.getItem(STORAGE_KEYS.maestroId);
    return value ? Number(value) : 0;
  }

  /** Obtiene el nombre del maestro en sesión. */
  getNombre(): string {
    return sessionStorage.getItem(STORAGE_KEYS.nombre) ?? '';
  }

  /** Sustituye el nombre de sesión por el del socio consultado. */
  setNombre(nombre: string): void {
    sessionStorage.setItem(STORAGE_KEYS.nombre, nombre);
  }

  /** Obtiene el RFC del maestro en sesión (reportes EDC / ficha de pago). */
  getRfc(): string {
    return sessionStorage.getItem(STORAGE_KEYS.rfc) ?? '';
  }

  /** Indica si el backend requiere cambio obligatorio de contraseña. */
  requiresPasswordReset(): boolean {
    const value = sessionStorage.getItem(STORAGE_KEYS.resetPasswd);
    return value === '1' || value === 'true';
  }

  /** Marca la contraseña como actualizada tras cambio exitoso. */
  clearPasswordResetRequirement(): void {
    sessionStorage.setItem(STORAGE_KEYS.resetPasswd, '0');
  }

  /** Limpia todos los datos de sesión del portal. */
  clearSession(): void {
    // Comentario: recorre cada clave conocida para vaciar sessionStorage.
    Object.values(STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
  }
}
